import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'

import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import {
  getGlobalMaturityRateISO,
  getSecurityCheckFamiliesWithScores,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import {
  fetchGlobalAverageActionsPerSecurityControl,
  getSecurityControlsWithScores,
} from '../../redux/securityControls/securityControlsAction'
import { getMaturityRateForExistingControl } from '../../redux/existingControls/existingControlActions'
import { selectMaturityRateResponse } from '../../redux/existingControls/existingControlSelectors'
import { selectGlobalMaturityRateISO } from '../../redux/securityCheckFamily/securityCheckFamilySelector'
import { selectGlobalAverage } from '../../redux/securityControls/securityControlsSelector'
import IndicatorMaturiteConformitePdfTemplate from '../../Pdf/IndicatorMaturite&ConformitePdfTemplate'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import { AnalysisPageLoader } from '../../components/common/analysisLayout'
import MaturityRadar from '../../components/common/MaturityRadar'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import '../../components/common/maturity-analysis.css'
import '../../components/common/ListPageTable.css'
import '../../Pdf/maturity-analysis-pdf.css'
import { formatDecimal } from '../../utils/formatNumbers'

const TARGET = 3

function levelLabel(score: number, t: (k: string, o?: Record<string, unknown>) => unknown) {
  const pick = (key: string, fallback: string) =>
    String(t(key, { defaultValue: fallback }) ?? fallback)

  if (score >= 4.5)
    return pick('analysis.maturityAnalysis.levels.optimizing', 'Optimizing')
  if (score >= 3.5)
    return pick('analysis.maturityAnalysis.levels.managed', 'Managed')
  if (score >= 2.5)
    return pick('analysis.maturityAnalysis.levels.defined', 'Defined')
  if (score >= 1.5)
    return pick('analysis.maturityAnalysis.levels.repeatable', 'Repeatable')
  return pick('analysis.maturityAnalysis.levels.initial', 'Initial')
}

function barColor(v: number) {
  if (v >= 3) return '#0D9B87'
  if (v >= 2) return '#C98A16'
  return '#D14A3E'
}

function valueTone(v: number): 'coral' | 'amber' | 'teal' | 'muted' {
  if (!v) return 'muted'
  if (v >= 3) return 'teal'
  if (v >= 2) return 'amber'
  return 'coral'
}

const MaturityIndicatorsPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const pdf = useAnalysisPdfExport()
  const [loading, setLoading] = useState(true)

  const globalMaturityRateISOValue = useSelector(
    selectGlobalMaturityRateISO
  )?.globalMaturityISO
  const securityCheckFamilies = useSelector(
    (state: RootState) => state.securitycheckFamily.data
  )
  const maturityRates = useSelector(selectMaturityRateResponse)
  const averageActions = useSelector(selectGlobalAverage)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        const lang = i18n.language.toUpperCase()
        await Promise.all([
          dispatch(getGlobalMaturityRateISO()),
          dispatch(getSecurityCheckFamiliesWithScores(lang)),
          dispatch(getSecurityControlsWithScores(lang)),
          dispatch(getMaturityRateForExistingControl()),
          dispatch(fetchGlobalAverageActionsPerSecurityControl()),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, i18n.language])

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const radar = useMemo(() => {
    const families = (securityCheckFamilies || []).slice(0, 10)
    return {
      labels: families.map((item: any) => item.labelSCF || item.label || '—'),
      values: families.map((item: any) => Number(item.scfScore) || 0),
    }
  }, [securityCheckFamilies])

  const hbarRows = useMemo(() => {
    const fromRates =
      maturityRates?.data?.map((item) => ({
        code: '',
        name: item.label || '—',
        v: Number(item.maturityRate) || 0,
      })) || []

    if (fromRates.length) return fromRates

    // Fallback: top security-check families as bars
    return (securityCheckFamilies || []).slice(0, 10).map((item: any) => ({
      code: String(item.code || item.Code || '').slice(0, 4),
      name: item.labelSCF || item.label || '—',
      v: Number(item.scfScore) || 0,
    }))
  }, [maturityRates, securityCheckFamilies])

  const maturityScore = Number(globalMaturityRateISOValue) || 0
  const maturityPct = Math.min(100, Math.max(0, (maturityScore / 5) * 100))
  const avgRaw = averageActions?.AverageActionsPerControl
  const avgPct =
    avgRaw !== undefined && avgRaw !== null
      ? Math.min(100, Math.max(0, Number(avgRaw) * 100))
      : 0

  const maturityTone = valueTone(maturityScore)
  const avgTone = avgPct > 0 ? valueTone(avgPct / 20) : 'muted'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.maturityAnalysis.eyebrow', {
          defaultValue: 'Governance · Maturity Management',
        })}
        title={t('analysis.maturityAnalysis.pageTitle', {
          defaultValue: 'Maturity Analysis',
        })}
        subtitle={t('analysis.maturityAnalysis.subtitle', {
          date: computedOn,
          defaultValue: `Scores computed on ${computedOn} from the checks currently in scope. The dashed outline on the radar is the target level set for the programme.`,
        })}
        actions={
          <>
            <DetailButton
              variant="ghost"
              onClick={() => navigate('/maturite')}
            >
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.maturityAnalysis.backToScope', {
                defaultValue: 'Back to scope',
              })}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() => pdf.exportPdf('maturity-analysis')}
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={2} chartCount={2} />
      ) : (
        <>
      <div className="ma-kpi-grid">
        <div className="ma-kpi">
          <span
            className="ma-kpi-stamp"
            title={t('analysis.maturityAnalysis.computedOn', {
              date: computedOn,
            })}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
              <path d="M2.5 6.5h11M5.5 2.5v2M10.5 2.5v2" />
            </svg>
          </span>
          <p className="ma-kpi-eyebrow">
            {t('analysis.maturityAnalysis.globalMaturityRateISO')}
          </p>
          <div className={`ma-kpi-value is-${maturityTone}`}>
            {formatDecimal(maturityScore)}{' '}
            <span className="ma-kpi-suffix">/ 5</span>
          </div>
          <div className="ma-bar-track">
            <span
              className="ma-bar-fill"
              style={{
                width: `${maturityPct}%`,
                background: barColor(maturityScore),
              }}
            />
          </div>
          <p className="ma-kpi-desc">
            {t('analysis.maturityAnalysis.maturityKpiDesc', {
              level: Math.max(1, Math.round(maturityScore)),
              label: levelLabel(maturityScore, t),
              target: formatDecimal(TARGET),
            })}
          </p>
        </div>

        <div className="ma-kpi">
          <span
            className="ma-kpi-stamp"
            title={t('analysis.maturityAnalysis.computedOn', {
              date: computedOn,
            })}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
              <path d="M2.5 6.5h11M5.5 2.5v2M10.5 2.5v2" />
            </svg>
          </span>
          <p className="ma-kpi-eyebrow">
            {t(
              'analysis.maturityAnalysis.globalAverageActionsPerControl'
            )}
          </p>
          <div className={`ma-kpi-value is-${avgTone}`}>
            {formatDecimal(avgPct)}
            <span className="ma-kpi-suffix"> %</span>
          </div>
          <div className="ma-bar-track">
            <span
              className="ma-bar-fill"
              style={{
                width: `${avgPct}%`,
                background: avgPct > 0 ? '#0D9B87' : '#CDD8D6',
              }}
            />
          </div>
          <p className="ma-kpi-desc">
            {avgPct > 0
              ? t('analysis.maturityAnalysis.actionsKpiDesc')
              : t('analysis.maturityAnalysis.actionsKpiEmpty')}
          </p>
        </div>
      </div>

      {radar.labels.length > 0 ? (
        <div className="ma-card">
          <div className="ma-card-head">
            <h3>
              {t('analysis.maturityAnalysis.securityCheckFamilyScores')}
            </h3>
            <span className="ma-card-hint">
              <span className="ma-legend-swatch" />
              {t('analysis.maturityAnalysis.scfScores')}
              <span className="ma-legend-dash" />
              {t('analysis.maturityAnalysis.targetLegend', {
                target: formatDecimal(TARGET),
              })}
            </span>
          </div>
          <div className="ma-card-body">
            <MaturityRadar
              labels={radar.labels}
              values={radar.values}
              max={5}
              target={TARGET}
            />
          </div>
        </div>
      ) : null}

      {hbarRows.length > 0 ? (
        <div className="ma-card">
          <div className="ma-card-head">
            <h3>
              {t('analysis.maturityAnalysis.maturityRates')}
            </h3>
            <span className="ma-card-hint">
              {t('analysis.maturityAnalysis.hbarHint', {
                count: hbarRows.length,
              })}
            </span>
          </div>
          <div className="ma-card-body">
            <div className="ma-hbars">
              {hbarRows.map((row, idx) => {
                const pct = Math.min(100, Math.max(0, (row.v / 5) * 100))
                return (
                  <div className="ma-hbar-row" key={`${row.name}-${idx}`}>
                    <div className="ma-hbar-label">
                      {row.code ? (
                        <span className="ma-hbar-code">{row.code}</span>
                      ) : null}
                      {row.name}
                    </div>
                    <div className="ma-bar-track" style={{ marginTop: 0 }}>
                      <span
                        className="ma-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: barColor(row.v),
                        }}
                      />
                    </div>
                    <div className="ma-hbar-val">
                      {formatDecimal(row.v)} / 5
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : null}
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <IndicatorMaturiteConformitePdfTemplate
          ref={pdf.ref}
          computedOn={computedOn}
          maturityScore={maturityScore}
          avgPct={avgPct}
          radarLabels={radar.labels}
          radarValues={radar.values}
          hbarRows={hbarRows}
          target={TARGET}
        />
      </div>
    </div>
  )
}

export default MaturityIndicatorsPage
